import { Button, Color, Icon, Spacing, Table, TableCol, TablePagination } from 'zens-storybook'
import React from 'react'
import { StyleSheet, View, ViewProps } from 'react-native'
import { useTranslation } from 'react-i18next'
import { Schedule, useSchedules } from 'zens-store'

import { Link } from 'zens-router'

import { TablePaginated } from '../table'

import ScheduleEmpty from './empty'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  head: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: Spacing.S,
  },
  button: {
    marginLeft: Spacing.XS,
  },
  buttonInactive: {
    borderColor: Color.calmGrey,
  },
  iconInactive: {
    color: Color.metalGrey,
  },
  skeletonButton: {
    minWidth: 0,
  },
})

interface Data {
  data: Schedule
  onPress?: () => void
}

interface Props {
  companyId: number
  search?: string
  pagination?: TablePagination
  onPaginationChange?: (pagination: TablePagination) => void
}

const ScheduleTable = (props: Props) => {
  const { companyId, search, pagination = { page: 0, size: 10 }, onPaginationChange } = props
  const schedules = useSchedules({ companyId, search })
  const { t } = useTranslation()

  const cols: TableCol<Schedule>[] = [
    {
      key: 'name',
      title: t('Name'),
    },
  ]

  const rows: Data[] =
    schedules.data?.map((schedule) => ({
      data: schedule,
      href: `/companies/${companyId}/workspaces/schedules/${schedule.id}`,
    })) || []

  return (
    <View style={styles.container}>
      <View style={styles.head}>
        <Link href={`/companies/${companyId}/workspaces/schedules`} asChild>
          <Button left={<Icon name="menu" size={18} />} style={styles.button} light testID="Table.TableButton" />
        </Link>
        <Link href={`/companies/${companyId}/workspaces/schedules/grid`} asChild>
          <Button
            left={<Icon name="grid" size={18} style={styles.iconInactive} />}
            style={styles.button}
            light
            contentContainerStyle={styles.buttonInactive}
            testID="Table.GridButton"
          />
        </Link>
      </View>

      <TablePaginated
        rows={rows}
        cols={cols}
        pagination={pagination}
        count={rows.length}
        renderCount={() =>
          t('{{page}} of {{numberOfPages}}', {
            page: pagination.page + 1,
            numberOfPages: Math.ceil(rows.length / pagination.size),
          })
        }
        sizeLabel={t('Schedules per page')}
        maxSize={1000}
        onPaginationChange={onPaginationChange}
        ListEmptyComponent={<ScheduleEmpty companyId={companyId} search={search} />}
      />
    </View>
  )
}
interface SkeletonProps extends ViewProps {}

const Skeleton = (props: SkeletonProps) => {
  const { style, ...otherProps } = props
  const { t } = useTranslation()

  const cols: TableCol<Schedule>[] = [
    {
      key: 'name',
      title: t('Name'),
    },
  ]
  return (
    <View style={[styles.container, style]} {...otherProps}>
      <View style={styles.head}>
        <Button.Skeleton style={styles.button} contentStyle={styles.skeletonButton} />
        <Button.Skeleton style={styles.button} contentStyle={styles.skeletonButton} />
      </View>
      <Table.Skeleton cols={cols} hasAccessory />
    </View>
  )
}

ScheduleTable.Skeleton = Skeleton

export default ScheduleTable
